import type { HssBankHolidayItem, OpeningTimes } from '~/types'

/**
 * Formats date as: yyyy-MM-dd
 */
export const formatDate = (
  date: string | Date,
  format?: 'input' | 'display'
) => {
  const d = new Date(date)
  let month = '' + (d.getMonth() + 1)
  let day = '' + d.getDate()
  const year = d.getFullYear()

  if (month.length < 2) month = '0' + month
  if (day.length < 2) day = '0' + day

  if (format == 'input') {
    return [year, month, day].join('-')
  } else {
    return [day, month, year].join('.')
  }
}

const SHORT_DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']

const SHORT_MONTHS = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
]

/**
 * Formats date as: dd MMM (e.g. "05 Aug")
 *
 * Built from fixed arrays rather than `toLocaleDateString` on purpose —
 * ICU abbreviates September as "Sept" in some runtimes and "Sep" in
 * others, which would render differently on the server and the client
 * and trip hydration.
 */
export const formatDateShort = (date: string | Date) => {
  const d = new Date(date)
  const day = String(d.getDate()).padStart(2, '0')

  return `${day} ${SHORT_MONTHS[d.getMonth()]}`
}

/**
 * Formats date as: EEE d MMM (e.g. "Tue 12 Sep")
 */
export const formatDateWithWeekday = (date: string | Date) => {
  const d = new Date(date)

  return `${SHORT_DAYS[d.getDay()]} ${d.getDate()} ${SHORT_MONTHS[d.getMonth()]}`
}

/**
 * Works out the difference between two days and returns it in days
 */

export const daysBetween = (
  startDate: string | Date,
  endDate: string | Date
) => {
  const date1 = new Date(startDate)
  const date2 = new Date(endDate)

  // Calculating the time difference
  const diffTime = date2.getTime() - date1.getTime()

  // Calculating the no. of days between
  const diffDays = Math.round(diffTime / (1000 * 3600 * 24))

  // To display the final no. of days (result)
  return diffDays + ' days'
}

/**
 * Works out the days open and close times of a particular branch
 */

const daysOfWeek = [
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
  'sunday',
  'bankHolidays',
]

export const getBranchTimes = (t: OpeningTimes, d: number) => {
  return Object.entries(t).find(([key]) => key === daysOfWeek[d])?.[1]
}

/**
 * Check start date is not after end date
 */
export const checkValidStartDate = (startDate: string, endDate: string) => {
  const startComparison = new Date(startDate.replace(/-/g, '/'))
  const endComparison = new Date(endDate.replace(/-/g, '/'))

  return startComparison < endComparison
}

/**
 * Check end date is not before start date
 */
export const checkValidEndDate = (startDate: string, endDate: string) => {
  const startComparison = new Date(startDate.replace(/-/g, '/'))
  const endComparison = new Date(endDate.replace(/-/g, '/'))

  return endComparison > startComparison
}

/**
 * Check date not in the past
 */
export const checkDateInPast = (date: string) => {
  const today = new Date()
  const dateCheck = new Date(date)

  if (dateCheck.setHours(0, 0, 0, 0) < today.setHours(0, 0, 0, 0)) {
    return true
  }

  return false
}

/**
 * Check the number of days between two dates — the *gap*, floored at 1.
 *
 * Used to set `hire_days_requested` on the ordering flows, which expect the gap.
 * Note a same-day hire and a one-night hire both return 1. To show a duration
 * next to a Brenda charge use `countInclusiveDays` below instead.
 */
export const checkNumberOfDays = (
  startDate: string,
  endDate: string
): number => {
  const startComparison = new Date(startDate.replace(/-/g, '/'))
  const endComparison = new Date(endDate.replace(/-/g, '/'))
  const diff = Math.abs(startComparison.getTime() - endComparison.getTime())
  const days = Math.ceil(diff / (1000 * 3600 * 24))

  return days < 1 ? 1 : days
}

/**
 * Count a hire duration inclusively — 13th to 13th is 1 day, 13th to 14th is 2.
 *
 * This is the count Brenda's rate bands price against, so it's the one to show
 * the customer whenever a duration sits next to a charge. Deliberately NOT
 * `checkNumberOfDays` above: under that gap measure a same-day hire and a
 * one-night hire both come out as 1 day, so extending 13th->13th to 13th->14th
 * reads as a zero-day change while Brenda correctly charges the 1-day to 2-day
 * rate difference.
 *
 * Parsed as UTC so the October clocks-change can't add a phantom day.
 */
export const countInclusiveDays = (
  startDate: string,
  endDate: string
): number => {
  const [startYear, startMonth, startDay] = startDate.split('-').map(Number)
  const [endYear, endMonth, endDay] = endDate.split('-').map(Number)
  const diff =
    Date.UTC(endYear, endMonth - 1, endDay) -
    Date.UTC(startYear, startMonth - 1, startDay)

  return Math.round(diff / (1000 * 60 * 60 * 24)) + 1
}

/**
 * Check if date is a Saturday
 */
export const checkDayIsSaturday = (date: string) => {
  const dateCheck = new Date(date)

  if (dateCheck.getDay() == 6) {
    return true
  }

  return false
}

/**
 * Check if date is a Sunday
 */
export const checkDayIsSunday = (date: string) => {
  const dateCheck = new Date(date)

  if (dateCheck.getDay() == 0) {
    return true
  }

  return false
}

/**
 * Adds days to an ISO date string (yyyy-mm-dd)
 */
export const addDaysToIsoDate = (isoDate: string, days: number) => {
  // safest: treat as UTC to avoid DST issues
  const [y, m, d] = isoDate.split('-').map(Number)
  const date = new Date(Date.UTC(y, m - 1, d))
  date.setUTCDate(date.getUTCDate() + days)
  const yyyy = date.getUTCFullYear()
  const mm = String(date.getUTCMonth() + 1).padStart(2, '0')
  const dd = String(date.getUTCDate()).padStart(2, '0')
  return `${yyyy}-${mm}-${dd}`
}

/**
 * Ensures an end date is not a bank holiday or weekend.
 * - If endDate is already valid, returns it unchanged.
 * - If endDate is a bank holiday or weekend, bumps forward day-by-day until valid (up to maxBumps).
 *
 * @returns a valid endDate (YYYY-MM-DD) or null if none found within maxBumps
 */

const isBankHoliday = (holidays: HssBankHolidayItem[], date: string) =>
  holidays.some(h => h.date === date)

const isWeekend = (isoDate: string) => {
  const day = new Date(`${isoDate}T00:00:00Z`).getUTCDay()
  return day === 0 || day === 6 // Sunday = 0, Saturday = 6
}

export function getValidEndDate(
  endDate: string,
  bankHolidays: HssBankHolidayItem[],
  maxBumps: number = 12
): string | null {
  const isInvalidDate = (date: string) =>
    isWeekend(date) || isBankHoliday(bankHolidays, date)

  // (a) already valid
  if (!isInvalidDate(endDate)) {
    return endDate
  }

  // (b) bump forward until valid
  let candidate = endDate

  for (let i = 0; i < maxBumps; i++) {
    candidate = addDaysToIsoDate(candidate, 1)

    if (!isInvalidDate(candidate)) {
      return candidate
    }
  }

  // (c) no valid date found
  return null
}

/**
 * Gets today's date in the UK as a string (yyyy-mm-dd)
 */
export const getUkTodayString = () => {
  const now = new Date()

  const formatter = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Europe/London',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  })

  const parts = formatter.formatToParts(now)

  const get = (type: string) => parts.find(p => p.type === type)?.value ?? '00'

  return `${get('year')}-${get('month')}-${get('day')}`
}

/**
 * Formats an ISO date as: DD Month YYYY (e.g. 11 June 2026)
 */
export function formatDateLongUK(iso?: string): string {
  if (!iso) return ''
  const dt = new Date(iso)
  return new Intl.DateTimeFormat('en-GB', {
    day: '2-digit',
    month: 'long',
    year: 'numeric',
    timeZone: 'Europe/London',
  }).format(dt)
}

/**
 * Formats a date as: DD.MM.YY (e.g. 11.06.26)
 */
export function formatDateShortUK(date?: string): string {
  if (!date) return '—'
  if (date.includes('T')) {
    return new Intl.DateTimeFormat('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: '2-digit',
      timeZone: 'Europe/London',
    })
      .format(new Date(date))
      .replaceAll('/', '.')
  }
  const m = date.match(/^(\d{4})-(\d{2})-(\d{2})$/)
  if (m) {
    const [, y, mo, d] = m
    return `${d}.${mo}.${y.slice(-2)}`
  }
  return '—'
}

/**
 * Formats a HH:mm(:ss) time string as: HH:mm
 */
export function formatTimeUK(time?: string): string {
  if (!time) return '—'
  const [hStr = '0', mStr = '0'] = time.split(':')
  const h = Number(hStr)
  const m = Number(mStr)
  if (Number.isNaN(h) || Number.isNaN(m)) return time.slice(0, 5)

  const hh = String(h).padStart(2, '0')
  const mm = String(m).padStart(2, '0')
  return `${hh}:${mm}`
}

type ParseOpts = { endOfDay?: boolean }

/**
 * Parses an ISO date/datetime string to epoch ms (UTC), or null if invalid.
 * A date-only string can optionally resolve to the end of that day.
 */
export function parseDateToMs(s?: string, opts: ParseOpts = {}): number | null {
  if (!s) return null

  if (s.includes('T')) {
    const t = Date.parse(s)
    return Number.isNaN(t) ? null : t
  }

  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})$/)
  if (m) {
    const y = Number(m[1])
    const mo = Number(m[2])
    const d = Number(m[3])
    return opts.endOfDay
      ? Date.UTC(y, mo - 1, d, 23, 59, 59, 999)
      : Date.UTC(y, mo - 1, d)
  }

  const t = Date.parse(s)
  return Number.isNaN(t) ? null : t
}

/**
 * True if the end of the given day has been reached (now >= end of day).
 */
export function isReachedEnd(s?: string): boolean {
  const t = parseDateToMs(s, { endOfDay: true })
  return t != null && Date.now() >= t
}

/**
 * True if the given date/datetime has been reached (now >= it).
 */
export function isReached(s?: string): boolean {
  const t = parseDateToMs(s)
  return t != null && Date.now() >= t
}
